import {
  AttachFileOutlined,
  CloseRounded,
  EditOutlined,
  FileOpenOutlined,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { get, isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FlxGrid from "../../../../../_src_shared/components/layout/FlxGrid";
import PrimaryContentCard from "../../../../../_src_shared/components/layout/PrimaryContentCard";
import JsonPreview from "../../../../../components/common/JsonPreview";
import UpdateLoanRootDataFieldsModal from "../../../../../components/common/Loan/UpdateLoanRootDataFieldsModal";
import OverviewItem from "../../../../../components/data/OverviewItem";
import Flx from "../../../../../components/layout/Flx";
import TabPanel from "../../../../../components/navigation/TabPanel";
import ToggleTabSwitcher from "../../../../../components/navigation/ToggleTabSwitcher";
import Txt from "../../../../../components/typography/Txt";
import InsetCard from "../../../../../components/ui/InsetCard";
import { camelCaseToSentence } from "../../../../../utils/strings/camelCaseToSentence";
import LoanFilesTable from "../../LoanDrilldownFilesTab/LoanFilesTable/LoanFilesTable";
import LoanBorrowerAssignFilesModal from "./LoanBorrowerAssignFilesModal";
import useAssociationUtils from "../../../../../components/associations/hooks/useAssociationUtils";
import UploadLoanFileModal from "../../../../../components/modals/common/UploadLoanFileModal";
import BorrowerRequiredDocumentsChecklist from "./common/BorrowerRequiredDocumentsChecklist";
import AssignLoanFilesButton from "../../../../../components/common/Loan/buttons/AssignLoanFilesButton";
import BorrowerRequiredDocumentsChecklistTable from "./common/BorrowerRequiredDocumentsChecklistTable";

import { loanDrilldownSidebarTypes } from "../../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { sidebarOpen } from "../../../../../redux/reducers/sidebarReducer";

const LoanBorrowerDetailsCard = ({ borrower }) => {
  const navigate = useNavigate();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const TABS = useMemo(() => {
    //  ["Overview", "Details", "Files", "Checklist", "Raw JSON"]
    return {
      OVERVIEW: "Overview",
      DETAILS: "Details",
      FILES: "Associated Files",
      CHECKLIST: "Checklist",
      RAW_JSON: "Raw JSON",
    };
  }, []);
  const title = useMemo(
    () => `${borrower?.firstName} ${borrower?.lastName}`,
    [borrower?.firstName, borrower?.lastName],
  );
  const [tab, setTab] = useState(TABS.OVERVIEW);

  const onClose = useCallback((props) => {
    navigate(`/loan/${loanDrilldown?.loanNumber}/borrowers`);
  }, []);
  if (isNil(borrower)) {
    return null;
  }
  return (
    <PrimaryContentCard
      maxWidth="lg"
      title={title}
      headerSx={{ mb: 1 }}
      endContent={
        <Flx ac>
          {/* <AssignBorrowerFilesButton borrower={borrower} /> */}
          <AssignLoanFilesButton borrower={borrower} />
          <IconButton onClick={onClose}>
            <CloseRounded className="thin" />
          </IconButton>
        </Flx>
      }
      // subTitle={headerSubtitle}
      // loading={isNil(loanTerms)}
    >
      <ToggleTabSwitcher
        value={tab}
        onChange={setTab}
        variant="underline"
        tabs={Object.values(TABS)}
      />
      <TabPanel value={tab} tabValue={TABS.OVERVIEW}>
        <Flx column g={2}>
          <BorrowerEditableDataContent borrower={borrower} />
          <BorrowerFilesTable borrower={borrower} />
        </Flx>
      </TabPanel>
      <TabPanel value={tab} tabValue={TABS.DETAILS}>
        <Flx column g={2}>
          <BorrowerEditableDataContent borrower={borrower} />
        </Flx>
      </TabPanel>
      <TabPanel value={tab} tabValue={TABS.FILES}>
        <Flx column g={2}>
          <BorrowerFilesTable borrower={borrower} />
        </Flx>
      </TabPanel>
      <TabPanel value={tab} tabValue={TABS.CHECKLIST}>
        <Flx column g={2}>
          {/* <BorrowerRequiredDocumentsChecklistTable borrower={borrower} /> */}
          <BorrowerRequiredDocumentsChecklist borrower={borrower} />
        </Flx>
      </TabPanel>
      <TabPanel value={tab} tabValue={TABS.RAW_JSON}>
        <JsonPreview values={borrower} />
      </TabPanel>
    </PrimaryContentCard>
  );
};

const AssignBorrowerFilesButton = ({ borrower }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Tooltip title="Assign Files to Borrower">
        <IconButton onClick={() => setShow(true)}>
          {/* <AttachFileOutlined className="thin" /> */}
          <AttachFileOutlined className="thin" />
          {/* <FileOpenOutlined className="thin" /> */}
          {/* <AssignmentReturnOutlined className="thin" /> */}
        </IconButton>
      </Tooltip>
      <LoanBorrowerAssignFilesModal
        show={show}
        borrowerId={borrower?._id}
        onClose={() => setShow(false)}
      />
    </>
  );
};

const BorrowerEditableDataContent = ({ borrower }) => {
  const TAB_NAME_MAP = {
    ALL: "All",
    BORROWER_DETAILS: "Borrower Details & Identity",
    CONTACT_INFO: "Contact Information",
    CREDIT_AUTH_SCORES: "Credit Auth & Scores",
    CREDIT_EVENTS: "Credit Events",
    EXPERIENCE: "Experience",
    LEGAL_COMPLIANCE: "Legal & Compliance",
    LIQUIDITY_FINANCIALS: "Liquidity & Financials",
    NOTES: "Notes",
    ROLE_RELATIONSHIP: "Role & Relationship",
    SYSTEM_METADATA: "System & Metadata",
  };
  // const TABS = useMemo(
  //   () => [
  //     "Borrower Details",
  //     // "Identity & Personal",
  //     "Contact Info",
  //     "Credit Auth & Scores",
  //     "Credit Events",
  //     "Experience",
  //     "Legal & Compliance",
  //     "Liquidity & Financials",
  //     // "Experience Overview",
  //     // "Experience Overview",
  //     // "Experience Fix & Flip",
  //     // "Experience Ground-Up",
  //     // "Experience Income-Producing",
  //     "Notes",
  //     "Role & Relationship",
  //     "System & Metadata",
  //   ],
  //   []
  // );

  const TABS = useMemo(() => Object.values(TAB_NAME_MAP), []);
  const [tab, setTab] = useState(TABS[0]);

  return (
    <InsetCard grey sx={{ pt: 1 }}>
      {/* <FlxGrid width="300px" gap={8}>
        <Flx column jb gap={4}>
          <FlxGrid width="300px" gap={2.4} sx={{ mt: 2 }}></FlxGrid>
        </Flx>
        <Flx column>
          <Txt bold>Mortgage Clause</Txt>
          <InsetCard grey sx={{ p: 1.5 }}>
            address
          </InsetCard>
        </Flx>
      </FlxGrid> */}
      <Flx column gap={4}>
        <ToggleTabSwitcher
          variant={"circle"}
          value={tab}
          onChange={setTab}
          sx={{
            width: "calc(100% + 16px)",
            ml: -2,
            ".MuiButtonBase-root": {
              fontSize: "11px",
            },
          }}
          // variant="underline"
          tabs={TABS}
        />
        <BorrowerGroupIdentityPersonal
          activeTab={tab}
          tabName={TAB_NAME_MAP.BORROWER_DETAILS}
          borrower={borrower}
        />

        <BorrowerGroupContactInfo
          activeTab={tab}
          tabName={TAB_NAME_MAP.CONTACT_INFO}
          borrower={borrower}
        />
        <BorrowerGroupCreditAuthAndScores
          activeTab={tab}
          tabName={TAB_NAME_MAP.CREDIT_AUTH_SCORES}
          borrower={borrower}
        />

        <BorrowerGroupCreditDerogatoryEvents
          activeTab={tab}
          tabName={TAB_NAME_MAP.CREDIT_EVENTS}
          borrower={borrower}
        />
        <CustomSection
          gap={3}
          title={"Experience"}
          activeTab={tab}
          tabName={TAB_NAME_MAP.EXPERIENCE}
        >
          <BorrowerGroupExperienceOverview
            activeTab={tab}
            tabName={TAB_NAME_MAP.EXPERIENCE}
            borrower={borrower}
          />

          <BorrowerGroupExperienceFixFlip
            activeTab={tab}
            tabName={TAB_NAME_MAP.EXPERIENCE}
            borrower={borrower}
          />
          <BorrowerGroupExperienceGroundUp
            activeTab={tab}
            tabName={TAB_NAME_MAP.EXPERIENCE}
            borrower={borrower}
          />
          <BorrowerGroupExperienceIncomeProducing
            activeTab={tab}
            tabName={TAB_NAME_MAP.EXPERIENCE}
            borrower={borrower}
          />
        </CustomSection>
        <BorrowerGroupLegalComplianceDisclosures
          activeTab={tab}
          tabName={TAB_NAME_MAP.LEGAL_COMPLIANCE}
          borrower={borrower}
        />
        <BorrowerGroupLiquidityFinancialStrength
          activeTab={tab}
          tabName={TAB_NAME_MAP.LIQUIDITY_FINANCIALS}
          borrower={borrower}
        />

        <BorrowerGroupNotes
          activeTab={tab}
          tabName={TAB_NAME_MAP.NOTES}
          borrower={borrower}
        />
        <BorrowerGroupRoleRelationship
          activeTab={tab}
          tabName={TAB_NAME_MAP.ROLE_RELATIONSHIP}
          borrower={borrower}
        />
        <BorrowerGroupSystemMetadata
          activeTab={tab}
          tabName={TAB_NAME_MAP.SYSTEM_METADATA}
          borrower={borrower}
        />
      </Flx>
    </InsetCard>
  );
};

const BorrowerGroupIdentityPersonal = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      { name: "firstName", label: "First Name", type: "text" },
      { name: "lastName", label: "Last Name", type: "text" },
      { name: "dateOfBirth", label: "Date of Birth", type: "date" },
      {
        name: "maritalStatus",
        label: "Marital Status",
        type: "select",
        options: ["Married", "Not Married"],
      },
      {
        name: "citizenshipStatus",
        label: "Citizenship Status",
        type: "select",
        options: [
          "US Citizen",
          "Foreign National with SSN",
          "Foreign National",
          "Foreign National Non-Resident",
          "Other - Options do not apply",
        ],
      },
      {
        name: "foreignNationalFlag",
        label: "Foreign National",
        type: "booleanToggle",
      },
      {
        name: "borrowerHasSSN",
        label: "Has US Based SSN",
        type: "booleanToggle",
      },
      { name: "borrowerSSN", label: "Social Security Number", type: "ssn" },
      {
        name: "borrowerIdType",
        label: "Borrower ID Type",
        type: "select",
        options: ["Driver License", "State ID", "Passport", "Other"],
      },
      {
        name: "firstTimeHomebuyer",
        label: "First Time Homebuyer",
        type: "booleanToggle",
      },
      {
        name: "company",
        label: "Company / Entity Name",
        type: "text",
      },
      {
        name: "entityPercentOwnership",
        label: "Entity % Ownership",
        type: "number",
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title={tabName}
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupContactInfo = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      { name: "emailAddress", label: "Email", type: "email" },
      { name: "phone", label: "Phone", type: "phone" },
      {
        name: "primaryAddress",
        label: "Primary Address",
        type: "addressEditable",
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title={tabName}
      // title="Contact Information"
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupRoleRelationship = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      {
        name: "role",
        label: "Borrower Role",
        type: "select",
        options: ["Borrower", "Co-Borrower", "Guarantor", "Other"],
      },
      { name: "isGuarantor", label: "Is Guarantor", type: "booleanToggle" },
      {
        name: "directBorrower",
        label: "Direct Borrower",
        type: "booleanToggle",
      },
      {
        name: "isPrimaryContactForLoan",
        label: "Primary Contact for Loan",
        type: "booleanToggle",
      },
      {
        name: "repeatBorrowerFlag",
        label: "Repeat Borrower",
        type: "booleanToggle",
      },
      {
        name: "broker",
        label: "Broker",
        type: "text",
      },
      {
        name: "loanNumber",
        label: "Loan Number",
        type: "text",
        readOnly: true,
      },
      {
        name: "lq_id_from_tag",
        label: "Loan Qualified ID (Tag)",
        type: "text",
        readOnly: true,
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title={tabName}
      // title="Role & Relationship to Loan"
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupCreditAuthAndScores = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      {
        name: "creditAuthDate",
        label: "Credit Authorization Date",
        type: "date",
      },
      { name: "creditReportDate", label: "Credit Report Date", type: "date" },
      { name: "estimatedFICO", label: "Estimated FICO", type: "number" },
      { name: "ficoLow", label: "FICO Low", type: "number" },
      { name: "ficoMid", label: "FICO Mid", type: "number" },
      { name: "ficoMax", label: "FICO High", type: "number" },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title={tabName}
      // title="Credit Authorization & Scores"
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupCreditDerogatoryEvents = ({
  borrower,
  tabName,
  activeTab,
}) => {
  const fields = useMemo(
    () => [
      { name: "bankruptcy_flag", label: "Bankruptcy", type: "booleanToggle" },
      { name: "charge_offs", label: "Charge-Offs", type: "number" },
      { name: "mortgage_lates", label: "Mortgage Lates", type: "number" },
      {
        name: "open_liens_judgments",
        label: "Open Liens / Judgments",
        type: "number",
      },
      {
        name: "financial_judgements",
        label: "Financial Judgments",
        type: "number",
      },
      { name: "tax_liens", label: "Tax Liens", type: "number" },
      { name: "housing_events", label: "Housing Events", type: "number" },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title={tabName}
      // title="Credit Events & Adverse History"
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupLegalComplianceDisclosures = ({
  borrower,
  tabName,
  activeTab,
}) => {
  const fields = useMemo(
    () => [
      {
        name: "application_felony_conviction",
        label: "Felony Conviction",
        type: "booleanToggle",
      },
      {
        name: "application_financial_fraud_misdemeanor",
        label: "Financial Fraud / Misdemeanor",
        type: "booleanToggle",
      },
      { name: "backgroundDate", label: "Background Check Date", type: "date" },
      {
        name: "borrowerGuidelineChecks",
        label: "Guideline Checks",
        type: "textArea",
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title={tabName}
      // title="Legal & Compliance Disclosures"
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupLiquidityFinancialStrength = ({
  borrower,
  tabName,
  activeTab,
}) => {
  const fields = useMemo(
    () => [
      { name: "cashLiquidity", label: "Cash Liquidity", type: "currency" },
      { name: "otherLiquidity", label: "Other Liquidity", type: "currency" },
      { name: "totalLiquidity", label: "Total Liquidity", type: "currency" },
      {
        name: "verifiedLiquidity",
        label: "Verified Liquidity",
        type: "currency",
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title={tabName}
      // title="Liquidity & Financial Strength"
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupExperienceOverview = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      {
        name: "isExperienced",
        label: "Experienced Borrower",
        type: "booleanToggle",
      },

      {
        name: "hasExperienceWithFixFlips",
        label: "Has Fix & Flip Experience",
        type: "booleanToggle",
      },
      {
        name: "hasExperienceWithGroundUpConstruction",
        label: "Has Ground-Up Construction Experience",
        type: "booleanToggle",
      },
      {
        name: "hasExperienceWithIncomeProducingProperties",
        label: "Has Income-Producing Property Experience",
        type: "booleanToggle",
      },
      {
        name: "verifiedExperienceTotal",
        label: "Verified Experience Total",
        type: "number",
      },
      {
        name: "borrowerExperienceSummary",
        label: "Experience Summary",
        type: "textArea",
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title="Experience Overview"
      fields={fields}
      tabName={tabName}
      gridWidth={"300px"}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupExperienceFixFlip = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      {
        name: "ff_sold.less_than_three_years",
        label: "Sold (< 3 yrs)",
        type: "number",
      },
      {
        name: "ff_sold.greater_than_three_years",
        label: "Sold (> 3 yrs)",
        type: "number",
      },
      {
        name: "ff_retained_as_rental.less_than_three_years",
        label: "Retained as Rental (< 3 yrs)",
        type: "number",
      },
      {
        name: "ff_retained_as_rental.greater_than_three_years",
        label: "Retained as Rental (> 3 yrs)",
        type: "number",
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title="Fix & Flip Property Experience"
      fields={fields}
      tabName={tabName}
      // gridWidth={"300px"}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupExperienceGroundUp = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      // {
      //   name: "general_contractor_projects",
      //   label: "GC Projects",
      //   type: "number",
      // },
      // { name: "guc_sold", label: "Ground-Up - Sold", type: "number" },
      {
        name: "general_contractor_projects.less_than_three_years",
        label: "General Contractor Projects (< 3 yrs)",
        type: "number",
      },
      {
        name: "general_contractor_projects.greater_than_three_years",
        label: "General Contractor Projects (> 3 yrs)",
        type: "number",
      },
      {
        name: "guc_sold.less_than_three_years",
        label: "GUC - Sold (< 3 yrs)",
        type: "number",
      },
      {
        name: "guc_sold.greater_than_three_years",
        label: "GUC - Sold (> 3 yrs)",
        type: "number",
      },
      {
        name: "guc_retained_as_rental.less_than_three_years",
        label: "GUC - Retained as Rental (< 3 yrs)",
        type: "number",
      },
      {
        name: "guc_retained_as_rental.greater_than_three_years",
        label: "GUC - Retained as Rental (> 3 yrs)",
        type: "number",
      },
      // {
      //   name: "guc_retained_as_rental",
      //   label: "Ground-Up - Retained as Rental",
      //   type: "number",
      // },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title="Ground-Up Construction Experience"
      fields={fields}
      gridWidth={"300px"}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupExperienceIncomeProducing = ({
  borrower,
  tabName,
  activeTab,
}) => {
  const fields = useMemo(
    () => [
      // {
      //   name: "hasExperienceWithIncomeProducingProperties",
      //   label: "Has Income-Producing Property Experience",
      //   type: "booleanToggle",
      // },
      // {
      //   name: "income_producing_properties",
      //   label: "Income-Producing Properties",
      //   type: "number",
      // },
      {
        name: "income_producing_properties.less_than_three_years",
        label: "Income-Producing Properties (< 3 yrs)",
        type: "number",
      },
      {
        name: "income_producing_properties.greater_than_three_years",
        label: "Income-Producing Properties (> 3 yrs)",
        type: "number",
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title="Income-Producing Property Experience"
      fields={fields}
      gridWidth={"300px"}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupNotes = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      {
        name: "additional_borrower_notes",
        label: "Additional Borrower Notes",
        type: "textarea",
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title="Notes"
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const BorrowerGroupSystemMetadata = ({ borrower, tabName, activeTab }) => {
  const fields = useMemo(
    () => [
      {
        name: "createDateTime",
        label: "Created Date/Time",
        type: "dateTime",
        readOnly: true,
      },
      {
        name: "signUpDate",
        label: "Sign Up Date",
        type: "date",
        readOnly: true,
      },
      {
        name: "profileComplete",
        label: "Profile Complete",
        type: "booleanToggle",
        readOnly: true,
      },
    ],
    [],
  );

  return (
    <EditableBorrowerSection
      title="System & Metadata"
      fields={fields}
      tabName={tabName}
      activeTab={activeTab}
      borrower={borrower}
    />
  );
};

const EditableBorrowerSection = ({
  children,
  title,
  fields,
  borrower,
  size = "xs",
  onNavigate,
  gridWidth = "165px",
  gridGrow = 1,
  gridGap = 3,
  tabName,
  activeTab,
}) => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const dispatch = useDispatch();
  const onEditClick = useCallback(() => {
    console.log(
      "onEditClick -> Fields schema that will render the form in the sidebar:",

      fields,
    );
    dispatch(
      sidebarOpen(loanDrilldownSidebarTypes.EDIT_BORROWER, borrower, {
        fields,
      }),
    );
    // if (fields) {
    //   setShow(true);
    // }

    if (onNavigate) {
      navigate(`/loan/${loanDrilldown?.loanNumber}/${onNavigate}`);
    }
  }, [fields, onNavigate, loanDrilldown?.loanNumber]);

  if (tabName !== activeTab && activeTab !== "All") {
    return null;
  }
  return (
    <>
      <UpdateLoanRootDataFieldsModal
        show={show}
        maxWidth={size}
        // formSpy
        borrowerId={borrower?._id}
        onClose={() => setShow(false)}
        fields={fields}
      />

      <CustomSection title={title}>
        <FlxGrid grow={gridGrow} width={gridWidth} gap={gridGap}>
          {(fields || onNavigate) && (
            <Tooltip title="Edit Details">
              <IconButton
                size="small"
                onClick={onEditClick}
                sx={{ position: "absolute", top: 4, right: 4 }}
              >
                <EditOutlined className="thin" sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {children}
          {fields.map((field) => (
            <DisplayData
              key={field.name}
              name={field.name}
              label={field.label}
              type={field.type}
              data={borrower}
            />
          ))}
        </FlxGrid>
      </CustomSection>
    </>
  );
};

const CustomSection = ({ title, children, gap = 3, activeTab, tabName }) => {
  if (tabName !== activeTab && activeTab !== "All") {
    return null;
  }
  return (
    <Flx column gap={"3px"}>
      {title && (
        <>
          <Txt sx={{ fontWeight: 300, color: grey[800] }} fontSize={12}>
            {title}
          </Txt>
        </>
      )}
      <Flx
        column
        sx={{
          position: "relative",
          border: "1px solid #e5e7eb",
          p: 2.5,
          gap,
          background: "#ffffff",
          borderRadius: 1.5,
        }}
      >
        {children}
      </Flx>
    </Flx>
  );
};

const DisplayData = ({ label, value, dataKey, type, name, data }) => {
  // const targetData = useSelector((state) => state?.loanFetchedData?.loanTerms);

  const passedData = useMemo(() => data, [data]);

  const val = useMemo(() => {
    const targetData = isNil(dataKey) ? passedData : passedData?.[dataKey];

    // Preserve prior behavior: if `value` is provided, use it.
    // (Note: this keeps the existing "truthy" behavior; if you want `0` to show,
    // change to `if (!isNil(value)) return value;`.)
    if (value) return value;

    // Supports both flat keys ("foo") and dot-notation ("guc_sold.greater_than_three_years")
    return isNil(name) ? undefined : get(targetData, name);
  }, [name, value, passedData, dataKey]);

  const labelValue = useMemo(() => {
    if (isNil(label)) {
      // If name is dot-notation, labelize the last segment (e.g. "greater_than_three_years")
      const labelSource =
        typeof name === "string" ? name.split(".").pop() : name;
      return camelCaseToSentence(labelSource);
    }
    return label;
  }, [label, name]);

  return (
    <OverviewItem
      label={labelValue}
      type={type}
      value={val}
      sx={{ flexGrow: 1 }}
    />
  );
};

// const DisplayData = ({ label, value, dataKey, type, name, data }) => {
//   // const targetData = useSelector((state) => state?.loanFetchedData?.loanTerms);
//   const passedData = useMemo(() => data, [data]);
//   const val = useMemo(() => {
//     const targetData = isNil(dataKey) ? passedData : passedData?.[dataKey];
//     if (value) {
//       return value;
//     }
//     return targetData?.[name];
//   }, [name, value, passedData, dataKey]);

//   const labelValue = useMemo(() => {
//     if (isNil(label)) {
//       // Convert camelCase name to Title Case label
//       return camelCaseToSentence(name);
//     }
//     return label;
//   }, [label, name]);
//   return (
//     <OverviewItem
//       label={labelValue}
//       type={type}
//       value={val}
//       sx={{ flexGrow: 1 }}
//     />
//   );
// };

const BorrowerFilesTable = ({ borrower, tabName, activeTab }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { getAssociationDocumentsByBorrowerId } = useAssociationUtils();
  // const loanDocuments = loanDrilldown?.loanDocuments;
  // const borrowerAssociations = useMemo(
  //   () => borrower?.associations,
  //   [borrower?.associations]
  // );
  // const borrowerAssociationIds = borrowerAssociations?.loanDocuments;

  // const borrowerFiles = useMemo(() => {
  //   return loanDocuments?.filter((doc) =>
  //     borrowerAssociationIds?.includes(doc._id)
  //   );
  // }, [loanDocuments, borrowerAssociationIds]);

  const borrowerFiles = getAssociationDocumentsByBorrowerId(
    loanDrilldown,
    borrower?._id,
  );
  // const borrowerFiles = [];
  return <LoanFilesTable rowData={borrowerFiles} />;
};
export default LoanBorrowerDetailsCard;
